import { Component, inject, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { SasCheckboxComponent } from '../../sas-checkbox/sas-checkbox.component';

const BOX = 'B';
const INVALID = 'I';
const VALID = 'V';

@Component({
    selector: 'sas-switch-field-type',
    standalone: true,
    imports: [ReactiveFormsModule, FormlyModule, TranslatePipe, FormsModule, SasCheckboxComponent],
    template: `
    @if (!(useRemove && isRemoved)) {
        <div class="form-group mb-3 sas-switch">
            <span [id]="field.id + 'Label'"
                  class="mb-2 fw-semibold"
                  [textContent]="field.props.label"
                  translate="{{field.props.label}}">
            </span>
            @if (helpText.length) {
            <div [title]="helpText" class="sas-inline-block">
                <i class="bi bi-info-circle-fill sas-info-icon" aria-hidden="true"></i>
            </div>
            }
            <div class="sas-switch-box" [class]="stateClass">
                <button type="button" class="sas-switch-action" (click)="selectAction($event, invalid, false)">
                    {{ 'SWITCH.INVALID' | translate: { Default: 'Invalid' } }}
                </button>
                <div class="form-switch">
                    <input class="form-check-input m-0"
                           type="checkbox"
                           role="switch"
                           id="{{field.id}}"
                           name="{{field.name}}"
                           autocomplete="off"
                           [attr.aria-label]="(field.props.label ?? '') | translate"
                           [checked]="formControl.value.value"
                           [attr.aria-checked]="formControl.value.value"
                           (click)="selectAction($event, box, true)">
                    <label class="visually-hidden" for="{{field.id}}">
                        {{ field.name ?? '' | translate }}
                    </label>
                </div>
                <button type="button" class="sas-switch-action" (click)="selectAction($event, valid, false)">
                    {{ 'SWITCH.VALID' | translate: { Default: 'Valid' } }}
                </button>
            </div>
            <pre class="visually-hidden">Value: {{switch.value}}</pre>
            <pre class="visually-hidden">Confirmed: {{switch.confirmed}}</pre>
            @if (useRemove) {
            <pre class="visually-hidden">Removed: {{switch.removed}}</pre>
            }
        </div>
        @if (useRemove) {
            <sas-checkbox [sasId]="field.id + '-remove'"
                          [(model)]="switch.removed"
                          [label]="'SWITCH.REMOVEIT' | translate: { Default: 'Remove' }"
                          [name]="field.name + '-remove'"
                          (modelChange)="onModify()">
            </sas-checkbox>
        }
    }
    `,
    styleUrl: './sas-switch-field-type.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class SasSwitchFieldTypeComponent extends FieldType<FieldTypeConfig> {
    private translate = inject(TranslateService);

    helpText: string = '';
    useRemove: boolean = false;
    isRemoved: boolean = false;
    switch: { value?: boolean; confirmed?: boolean; removed?: boolean } = {};
    stateClass: string = '';
    invalid: string = INVALID;
    valid: string = VALID;
    box: string = BOX;

    constructor() {
        super();
    }

    ngOnInit() {
        if (this.field.props['helptext']) {
            const helpText = this.translate.instant(this.field.props['helptext']);

            this.helpText = helpText === this.field.props['helptext'] ? '' : helpText;
        }

        this.useRemove = this.field.props['sasUseRemove'] ? this.field.props['sasUseRemove'] : false;
        this.switch = this.mapToReceive(this.formControl.value);
        this.isRemoved = this.switch.removed ? this.switch.removed : false;
        this.stateClass = this.updateStateClass(this.switch);
    }

    private mapToReceive(model: { value?: boolean; confirmed?: boolean; removed?: boolean }) {
        const switchModel = model ? structuredClone(model) : {};

        const hasValue = switchModel.value !== undefined && switchModel.value !== null;

        switchModel.value = hasValue ? switchModel.value : undefined;
        switchModel.confirmed = hasValue ? switchModel.confirmed : false;
        switchModel.removed = !!switchModel.removed;

        return switchModel;
    }

    selectAction(ev: Event, area: string, isBox: boolean) {
        ev.stopPropagation();

        this.switch.confirmed = true;

        switch (area) {
            case this.valid:
                this.switch.value = true;
                break;
            case this.invalid:
                this.switch.value = false;
                break;
            case this.box:
                if (isBox) this.switch.value = this.switch.value !== undefined ? !this.switch.value : true
                break;
        }

        this.stateClass = this.updateStateClass(this.switch);
        this.onModify();
    }

    onModify() {
        this.formControl.setValue(this.switch);
    }

    private updateStateClass({ value, confirmed }: { value?: boolean; confirmed?: boolean }) {
        switch (value) {
            case true:
                return confirmed ? 'valid' : 'valid lighter';
            case false:
                return confirmed ? '' : 'lighter';
            default:
                return 'gray grayer';
        };
    }
}
